import React, { useState } from 'react';
import { IRelationshipLandRegisterQuery } from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { IConfig } from '../../../../../../Interfaces/IConfig';
import { Box, Button, Grid, Typography } from '@mui/material';
import { checkString } from '../../../../../core/standardFunctions';
import { ILandParcelEntry } from '../../../../../../Interfaces/IRealEstate';
import { PurchaseContractDocumentSummary } from '../../../PurchaseContract/Components/Summary/PurchaseContractDocumentSummary';
import { ILegalClientFullObject } from '../../../../../../Interfaces/ILegalClient';
import { ClientInformationLegalClient } from '../../../../../generic/ClientInformationLegalClient';
import { initContactInformation } from '../../../../../../Interfaces/ILegalClientContactInformation';
import { initAddress } from '../../../../../../Interfaces/ILegalClientAddress';
import { uploadFetch } from '../../../../../../hooks/useFetch';
import { CustomCircularProgress } from '../../../../../generic/CustomCircularProgress';

interface IProps {
  configObject: IConfig;
  relationship: IRelationshipLandRegisterQuery;
  handleNext: Function;
  handleBack: Function;
}

export const LandRegisterQuerySummary: React.FC<IProps> = ({
  configObject,
  relationship,
  handleNext,
  handleBack,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [legalClient, setLegalClient] = useState({
    FirstName: '',
    idBusinessForm: 1,
    idCountry: 1,
    idLegalClient: -1,
    idLegalClientType: 1,
    idLegalClientUser: -1,
    isReceivingEmailEnabled: false,
    LastName: '',
    loginPossible: true,
    passwordResetRequired: true,
    syncWithArnoTop: false,
    ContactInformationArray: [initContactInformation()],
    AddressArray: [initAddress()],
  } as ILegalClientFullObject);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const uploadObject = {
      ...relationship,
      Members: [legalClient],
    };
    uploadFetch(
      '/legaltransaction/createLandRegisterQuery',
      true,
      uploadObject,
      () => {},
      () => {},
      () => {
        setIsLoading(false);
        handleNext();
      }
    );
  };

  if (isLoading) {
    return <CustomCircularProgress />;
  }
  return (
    <>
      <Box sx={{ mt: 5 }} />
      <Typography variant='h4' sx={{ mb: 3 }}>
        Abschluss
      </Typography>

      <Typography variant='h6'>Zusammenfassung</Typography>

      <Grid container spacing={2} sx={{ ml: 5, mt: 2 }}>
        {relationship.RealEstateArray.map((x, i) => (
          <React.Fragment key={`re-${x.idRealEstate}-${i}`}>
            {i !== 0 && (
              <Grid item xs={12}>
                <hr />
              </Grid>
            )}

            {relationship.RealEstateArray.length > 1 && (
              <>
                <Grid item sm={4}>
                  <b>Nr.:</b>
                </Grid>
                <Grid item sm={8}>
                  {i + 1}
                </Grid>
              </>
            )}

            <Grid item sm={4}>
              <b>Adresse:</b>
            </Grid>
            <Grid item sm={8}>
              {checkString(x.Street)} {checkString(x.StreetNr)}
            </Grid>
            <Grid item sm={4} />
            <Grid item sm={8}>
              {checkString(x.Postcode)} {checkString(x.City)}
            </Grid>

            <Grid item sm={4}>
              <b>Nachweis des berechtigten Interesses:</b>
            </Grid>
            <Grid item sm={8}>
              {
                relationship.data.ReasonLandRegisterQueryArray.find(
                  res => res.idRealEstate === x.idRealEstate
                )?.Reason
              }
            </Grid>

            {/* Neuer Abschnitt: Grundbuch‐Einträge */}
            {Array.isArray(x.LandParcelEntryArray) &&
            x.LandParcelEntryArray.length > 0 ? (
              <>
                {/* Divider VOR dem ersten Eintrag */}
                {x.LandParcelEntryArray.map(
                  (eintrag: ILandParcelEntry, idx: number) => (
                    <React.Fragment key={`gb-${x.idRealEstate}-${idx}`}>
                      {/* Divider zwischen Einträgen */}

                      {/* Überschrift pro Eintrag */}
                      <Grid item sm={12}>
                        <Typography
                          variant='subtitle1'
                          sx={{ mt: idx === 0 ? 2 : 1 }}
                        >
                          Grundbuch‐Eintrag {idx + 1}:
                        </Typography>
                      </Grid>

                      {/* Amtsgericht */}
                      <Grid item sm={4}>
                        <b>Amtsgericht:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {eintrag.Court
                          ? `${checkString(eintrag.Court.Court)} | ${checkString(
                              eintrag.Court.FederalState
                            )}`
                          : 'k.A.'}
                      </Grid>

                      {/* Grundbuch / Blatt */}
                      <Grid item sm={4}>
                        <b>Gemarkung / Blatt:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {`${checkString(eintrag.LandRegister)} / ${checkString(
                          eintrag.SheetLandRegister
                        )}`}
                      </Grid>

                      {/* Flur */}
                      <Grid item sm={4}>
                        <b>Flur:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {checkString(eintrag.Parcel)}
                      </Grid>

                      {/* Flurstück */}
                      <Grid item sm={4}>
                        <b>Flurstück:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {checkString(eintrag.LandParcel)}
                      </Grid>
                    </React.Fragment>
                  )
                )}
              </>
            ) : (
              <>
                <Grid item sm={4}>
                  <b>Grundbuchinformationen:</b>
                </Grid>
                <Grid item sm={8}>
                  Keine Einträge
                </Grid>
              </>
            )}
          </React.Fragment>
        ))}
      </Grid>

      <PurchaseContractDocumentSummary
        documentArray={relationship.DocumentArray}
      />

      <form onSubmit={handleSubmit}>
        <Box sx={{ mt: 5 }} />
        <ClientInformationLegalClient
          configObject={configObject}
          legalClient={legalClient}
          setLegalClient={setLegalClient}
        />

        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            variant='outlined'
            onClick={() => handleBack()}
            sx={{ mr: 1 }}
          >
            Zurück
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button type='submit' variant='contained'>
            Weiter
          </Button>
        </Box>
      </form>
    </>
  );
};
